import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { api } from "../../api";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Input } from "../ui/input";
import { ErrorState } from "../common/ErrorState";
import { GymLoader } from "../common/GymLoader";

const ACTOR_LABELS = {
  kairoo: "Kairoo staff", kairoo_emulation: "Kairoo (emulating)", user: "Gym staff",
  member: "Member", system: "System",
};

function actorLabel(actor) {
  if (!actor) return "—";
  const label = ACTOR_LABELS[actor.type] || actor.type;
  const ref = actor.kairoo_user_ref || actor.user_ref || actor.member_ref;
  return ref ? `${label} · ${ref.slice(-8)}` : label;
}

function formatAt(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// `GET /control/audit` (Kairoo-only, tenant_guard.py `read_audit`) already existed — this is the
// first UI surface for it. Reads the hash-chained `audit_event` collection for the ONE gym
// selected on GymDetail.jsx, so a Kairoo operator can answer "who did what" for a specific gym
// without ever seeing another org's events (the endpoint is scoped by `organization_id` server-
// side, same isolation as every other cross-tenant control-plane read).
export function AuditLogPanel({ orgId }) {
  const [search, setSearch] = useState("");
  const reducedMotion = useReducedMotion();

  const auditQ = useQuery({
    queryKey: ["control-audit", orgId],
    queryFn: async () => (await api.get("/control/audit", { params: { organization_id: orgId } })).data.events,
  });

  const filtered = useMemo(() => {
    const events = auditQ.data || [];
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) =>
      e.action.toLowerCase().includes(q) ||
      (e.target_type || "").toLowerCase().includes(q) ||
      actorLabel(e.actor).toLowerCase().includes(q)
    );
  }, [auditQ.data, search]);

  if (auditQ.isError) return <ErrorState error={auditQ.error} onRetry={() => auditQ.refetch()} title="Couldn't load the audit log" />;
  if (auditQ.isLoading) return <GymLoader label="Loading audit log…" />;

  return (
    <motion.div {...fadeUp(reducedMotion)} className="space-y-3">
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--scarlet-text-secondary)" }} />
        <Input
          data-testid={CONTROL.auditSearchInput}
          placeholder="Search action, actor, target…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>

      {filtered.length === 0 ? (
        <div data-testid={CONTROL.auditEmptyState} className="solid-surface rounded-[14px] p-8 text-center text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
          {search ? "No audit events match that search." : "No audit events recorded for this gym yet."}
        </div>
      ) : (
        <div className="solid-surface rounded-[14px] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid={CONTROL.auditTable}>
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--scarlet-text-secondary)" }}>When</th>
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--scarlet-text-secondary)" }}>Who</th>
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--scarlet-text-secondary)" }}>Action</th>
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--scarlet-text-secondary)" }}>Target</th>
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--scarlet-text-secondary)" }}>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice().reverse().map((e, i) => (
                <tr key={i} data-testid={CONTROL.auditRow(i)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap" style={{ color: "var(--scarlet-text-secondary)" }}>{formatAt(e.at)}</td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: "var(--scarlet-text)" }}>{actorLabel(e.actor)}</td>
                  <td className="px-4 py-2.5 font-medium" style={{ color: "var(--scarlet-text)" }}>
                    {e.action}
                    {e.security_relevant && (
                      <span className="ml-1.5 status-pill overdue" style={{ display: "inline-flex" }}>
                        <span className="dot" /> Security
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                    {e.target_type}{e.target_ref ? ` · ${String(e.target_ref).slice(-8)}` : ""}
                  </td>
                  <td className="px-4 py-2.5 text-xs capitalize" style={{ color: e.outcome === "success" ? "var(--status-active-text)" : "var(--status-overdue-text)" }}>
                    {e.outcome}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
